import React, { useState, useEffect } from "react";
import { View, Text, TextInput, StyleSheet, ScrollView, TouchableOpacity, Image, Animated } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../context/ApiContext";
import CreateProjectModal from "../components/CreateProjectModal";
import Notification from "../components/Notification";
import SharedHeader from "../components/SharedHeader";
import NavButton from "../components/NavButton";

const formatRelativeTime = (dateString) => {
	if (!dateString) return "Just now";

	const date = new Date(dateString);
	const now = new Date();
	const diffInSeconds = Math.floor((now - date) / 1000);

	if (diffInSeconds < 60) {
		return "Just now";
	}

	const diffInMinutes = Math.floor(diffInSeconds / 60);
	if (diffInMinutes < 60) {
		return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
	}

	const diffInHours = Math.floor(diffInMinutes / 60);
	if (diffInHours < 24) {
		return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
	}

	const diffInDays = Math.floor(diffInHours / 24);
	if (diffInDays < 30) {
		return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
	}

	const diffInMonths = Math.floor(diffInDays / 30);
	if (diffInMonths < 12) {
		return `${diffInMonths} month${diffInMonths === 1 ? "" : "s"} ago`;
	}

	const diffInYears = Math.floor(diffInDays / 365);
	return `${diffInYears} year${diffInYears === 1 ? "" : "s"} ago`;
};

const ProjectCard = ({ id, title, type, lastUpdate, thumbnail, project, onDelete, isNew }) => {
	const navigate = useNavigate();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [highlightOpacity] = useState(new Animated.Value(isNew ? 1 : 0));

	useEffect(() => {
		if (isNew) {
			Animated.timing(highlightOpacity, {
				toValue: 0,
				duration: 3000,
				useNativeDriver: false,
			}).start();
		}
	}, [isNew]);

	const handleDeleteClick = (e) => {
		e.stopPropagation();
		setShowDeleteConfirm(true);
	};

	const handleConfirmDelete = async (e) => {
		e.stopPropagation();
		await onDelete(id);
		setShowDeleteConfirm(false);
	};

	const handleCancelDelete = (e) => {
		e.stopPropagation();
		setShowDeleteConfirm(false);
	};

	const cardStyle = {
		...styles.projectCard,
		backgroundColor: highlightOpacity.interpolate({
			inputRange: [0, 1],
			outputRange: ["#2A2A2A", "#0A84FF22"],
		}),
		borderColor: highlightOpacity.interpolate({
			inputRange: [0, 1],
			outputRange: ["transparent", "#0A84FF"],
		}),
	};

	return (
		<Animated.View style={cardStyle}>
			<TouchableOpacity onPress={() => navigate(`/app/${id}`, { state: { project } })} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
				<View style={styles.projectThumbnail}>
					{thumbnail ? (
						<Image source={{ uri: thumbnail }} style={styles.thumbnailImage} />
					) : (
						<View style={styles.thumbnailPlaceholder}>
							{type === "iOS" && <Ionicons name="logo-apple" size={40} color="#444" />}
							{type === "Android" && <Ionicons name="logo-google-playstore" size={40} color="#444" />}
							{type === "Web" && <Ionicons name="globe-outline" size={40} color="#444" />}
						</View>
					)}
				</View>
				<View style={styles.projectInfo}>
					<Text style={styles.projectTitle}>{title}</Text>
					<View style={styles.projectMeta}>
						<View style={styles.projectTypeContainer}>
							{type === "iOS" && <Ionicons name="logo-apple" size={16} color="#888" />}
							{type === "Android" && <Ionicons name="logo-google-playstore" size={16} color="#888" />}
							{type === "Web" && <Ionicons name="globe-outline" size={16} color="#888" />}
						</View>
						<Text style={styles.updateText}>Updated {lastUpdate}</Text>
					</View>
				</View>
				{isHovered && (
					<TouchableOpacity style={[styles.deleteButton, { opacity: isHovered ? 1 : 0 }]} onPress={handleDeleteClick}>
						<Ionicons name="trash-outline" size={20} color="#ff4444" />
					</TouchableOpacity>
				)}
				{showDeleteConfirm && (
					<View style={styles.deleteConfirm}>
						<Text style={styles.deleteConfirmText}>Delete this project?</Text>
						<View style={styles.deleteConfirmButtons}>
							<TouchableOpacity style={[styles.deleteConfirmButton, styles.deleteConfirmCancel]} onPress={handleCancelDelete}>
								<Text style={styles.deleteConfirmButtonText}>Cancel</Text>
							</TouchableOpacity>
							<TouchableOpacity style={[styles.deleteConfirmButton, styles.deleteConfirmDelete]} onPress={handleConfirmDelete}>
								<Text style={[styles.deleteConfirmButtonText, styles.deleteConfirmButtonTextDelete]}>Delete</Text>
							</TouchableOpacity>
						</View>
					</View>
				)}
			</TouchableOpacity>
		</Animated.View>
	);
};

const ProjectCardSkeleton = () => (
	<View style={[styles.projectCard, styles.skeletonCard]}>
		<View style={[styles.projectThumbnail, styles.skeletonThumbnail]} />
		<View style={styles.projectInfo}>
			<View style={styles.skeletonTitle} />
			<View style={styles.projectMeta}>
				<View style={styles.skeletonUpdateText} />
			</View>
		</View>
	</View>
);

const CreateNewCard = ({ onPress }) => (
	<TouchableOpacity style={[styles.projectCard, styles.createNewCard]} onPress={onPress}>
		<View style={styles.createNewContent}>
			<Ionicons name="add-circle-outline" size={40} color="#666" />
			<Text style={styles.createNewText}>New Project</Text>
		</View>
	</TouchableOpacity>
);

const WhatsNewSection = () => {
	//IF ASKED TO ADD A NEW VERSION, ADD IT TO THE TOP OF THE LIST. INCREMENT THE VERSION NUMBER UNLESS SPECIFIED.
	//If asked to add a new feature, add it to the top most item and resummarize the title, description and changes with ALL features.
	//append the current branch id "EXE-###" to the fixes array if it exists. ONLY create an array if it's a new verion
	//ensure the changes dont exceeed 4 changes.
	//ensure the "Whats New" badge text in the header is the top most version number.
	//These are customer facing logs, so make sure they are clear and concise and dont contain any technical details like CRUD or API calls.
	//do NOT just make up features. use git diff to find the changes.. ensure our branch id is included!
	//ensure the version is also set in /web/app.json as the expo.version! frontend and backend are tied to the same version!!
	//ensure the version is also set in /template/app.json as the expo.version! frontend and backend are tied to the same version!!
	const changelogEntries = [
		{
			id: 7,
			version: "0.1.4",
			date: "May 24, 2025",
			title: "Discount Codes Management & Private Threads",
			description: "Create and manage discount codes for your events while enjoying enhanced community features with private thread management and improved visual indicators.",
			changes: [
				"Create custom discount codes with percentage-based discounts",
				"Set usage limits and track redemptions for each discount code",
				"Private thread indicators with lock icons for easy identification",
				"User invitation system for private thread access management",
			],
			branches: ["EXE-96", "EXE-97"],
		},
		{
			id: 6,
			version: "0.1.3",
			date: "May 20, 2025",
			title: "Enhanced TestFlight Integration, Event Editing, Recurring Events & QR Scanner",
			description: "Real-time build status tracking with improved TestFlight upload experience, comprehensive event editing capabilities, recurring events support, QR code scanning functionality, and enhanced backend triaging.",
			changes: [
				"Event editing functionality with full update/edit/delete operations",
				"Recurring events support with flexible scheduling options",
				"QR code scanner integration for seamless event check-ins",
				"Real-time build status monitoring with automatic polling",
			],
			branches: ["EXE-102", "EXE-95", "EXE-94"],
		},
		{
			id: 5,
			version: "0.1.2",
			date: "May 17, 2025",
			title: "Small bugfixes and improvements",
			description: "We've fixed a few bugs and made some improvements to the app. Check them out below!",
			changes: ["Fixed a bug where the app would crash when trying to build a project", "Improved the performance of the app template", "Added api charges and call units to the dashboard", "Added concurrent users to the dashboard"],
		},
		{
			id: 4,
			version: "0.1.1",
			date: "May 13, 2025",
			title: "New Features and a fresh coat of paint",
			description: "We've added so many new features over the past few weeks. Check them out below!",
			changes: ["Native iOS Notifications", "Sell tickets to your events", "Follow your friends!", "Promote events and products in Feed", "Stripe integration for payments"],
		},
		{
			id: 1,
			version: "0.1.0",
			date: "April 27, 2025",
			title: "Initial Release",
			description: "The silent release of Executable. Create and manage your iOS projects with ease.",
			changes: ["Project creation wizard", "Supports TestFlight and App Store Connect submissions", "Canvas view for building your app", "User authentication and account management"],
		},
	];

	return (
		<View style={styles.whatsNewContainer}>
			<Text style={styles.whatsNewTitle}>What's New in Executable</Text>
			<Text style={styles.whatsNewSubtitle}>Stay up to date with the latest features and improvements</Text>

			{changelogEntries.map((entry) => (
				<View key={entry.id} style={styles.changelogEntry}>
					<View style={styles.changelogHeader}>
						<View style={styles.versionContainer}>
							<Text style={styles.versionText}>{entry.version}</Text>
						</View>
						<Text style={styles.dateText}>{entry.date}</Text>
					</View>

					<Text style={styles.entryTitle}>{entry.title}</Text>
					<Text style={styles.entryDescription}>{entry.description}</Text>

					{entry.branches && entry.branches.length > 0 && (
						<View style={styles.branchesContainer}>
							<Text style={styles.branchesLabel}>Fixes:</Text>
							<View style={styles.branchesList}>
								{entry.branches.map((branch, index) => (
									<View key={index} style={styles.branchTag}>
										<Ionicons name="git-branch-outline" size={14} color="#666" style={styles.branchIcon} />
										<Text style={styles.branchText}>{branch}</Text>
									</View>
								))}
							</View>
						</View>
					)}

					<View style={styles.changesList}>
						{entry.changes.map((change, index) => (
							<View key={index} style={styles.changeItem}>
								<Ionicons name="checkmark-circle" size={18} color="#4d2" style={styles.changeIcon} />
								<Text style={styles.changeText}>{change}</Text>
							</View>
						))}
					</View>
				</View>
			))}
		</View>
	);
};

const Home = () => {
	const { user, logout } = useAuth();
	const { api } = useApi();
	const navigate = useNavigate();
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [projects, setProjects] = useState([]);
	const [loading, setLoading] = useState(true);
	const [notifications, setNotifications] = useState([]);
	const [newProjectId, setNewProjectId] = useState(null);
	const [activeSection, setActiveSection] = useState("projects");

	const addNotification = (message, type = "error", project = null) => {
		const id = Date.now();
		const now = new Date();
		const timestamp = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

		setNotifications((prev) => [
			...prev,
			{
				id,
				message,
				type,
				project,
				timestamp,
				read: false,
			},
		]);
	};

	const handleDismissNotification = (id) => {
		if (id === "all") {
			setNotifications([]);
		} else {
			setNotifications((prev) => prev.filter((n) => n.id !== id));
		}
	};

	const fetchProjects = async () => {
		try {
			const response = await api("/app");
			setProjects(response);
		} catch (err) {
			console.error("Error fetching projects:", err);
			addNotification("Failed to fetch projects: " + err.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchProjects();
	}, [api]);

	useEffect(() => {
		if (newProjectId) {
			const timer = setTimeout(() => {
				setNewProjectId(null);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [newProjectId]);

	const handleCreateProject = async (projectData) => {
		try {
			setLoading(true);
			const project = await api("/app", {
				method: "POST",
				body: projectData,
			});

			setProjects((prev) => [project, ...prev]);
			setNewProjectId(project.id);
			setShowCreateModal(false);
			addNotification("Project created successfully!", "success", project);

			fetchProjects();
		} catch (err) {
			console.error("Error creating project:", err);
			addNotification("Failed to create project: " + err.message, "error", projectData);
		} finally {
			setLoading(false);
		}
	};

	const handleDeleteProject = async (projectId) => {
		try {
			setLoading(true);
			await api("/app", {
				method: "DELETE",
				body: { projectId },
			});

			const deletedProject = projects.find((p) => p.id === projectId);
			setProjects(projects.filter((p) => p.id !== projectId));
			addNotification("Project deleted successfully!", "success", deletedProject);
		} catch (err) {
			console.error("Error deleting project:", err);
			const failedProject = projects.find((p) => p.id === projectId);
			addNotification("Failed to delete project: " + err.message, "error", failedProject);
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = () => {
		logout();
		navigate("/login");
	};

	return (
		<View style={styles.container}>
			<SharedHeader
				user={user}
				onLogout={handleLogout}
				onNavigateToSettings={() => navigate("/settings")}
				notifications={notifications}
				onDismissNotification={handleDismissNotification}
				onNavigateToProjects={() => setActiveSection("projects")}
				isProjectsActive={activeSection === "projects"}>
				<NavButton icon="sparkles-outline" label="What's New!" isActive={activeSection === "whatsnew"} onClick={() => setActiveSection("whatsnew")} hasBadge={true} badgeText="0.1.4" />
			</SharedHeader>

			<ScrollView style={styles.content}>
				{activeSection === "projects" ? (
					<View style={styles.projectsGrid}>
						<CreateNewCard onPress={() => setShowCreateModal(true)} />
						{loading ? (
							<>
								<ProjectCardSkeleton />
							</>
						) : (
							projects.map((project) => (
								<ProjectCard
									key={project.id}
									id={project.id}
									title={project.name}
									type={project.type}
									lastUpdate={formatRelativeTime(project.updatedAt)}
									thumbnail={project.icon}
									project={project}
									onDelete={handleDeleteProject}
									isNew={project.id === newProjectId}
								/>
							))
						)}
					</View>
				) : activeSection === "whatsnew" ? (
					<WhatsNewSection />
				) : null}
			</ScrollView>

			{showCreateModal && <CreateProjectModal onClose={() => setShowCreateModal(false)} onCreateProject={handleCreateProject} />}
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#1A1A1A",
	},
	title: {
		fontSize: 24,
		fontWeight: "bold",
		color: "#fff",
		padding: 20,
		paddingBottom: 0,
	},
	content: {
		flex: 1,
		padding: 20,
		paddingTop: 30,
	},
	projectsGrid: {
		flexDirection: "row",
		flexWrap: "wrap",
		gap: 24,
	},
	projectCard: {
		width: 256,
		height: 256,
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		overflow: "hidden",
		position: "relative",
		borderWidth: 2,
		borderColor: "transparent",
		":hover": {
			backgroundColor: "#333333",
		},
	},
	createNewCard: {
		backgroundColor: "transparent",
		borderWidth: 1,
		borderColor: "#2A2A2A",
		borderStyle: "dashed",
		height: 256,
		width: 256,
		justifyContent: "center",
		alignItems: "center",
	},
	createNewContent: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
		gap: 12,
		width: "100%",
		height: "100%",
	},
	createNewText: {
		color: "#666",
		fontSize: 16,
		fontWeight: "500",
	},
	projectThumbnail: {
		width: "100%",
		height: 170,
		backgroundColor: "#333",
		justifyContent: "center",
		alignItems: "center",
	},
	thumbnailImage: {
		width: "100%",
		height: "100%",
		resizeMode: "cover",
	},
	thumbnailPlaceholder: {
		flex: 1,
		backgroundColor: "#333",
		justifyContent: "center",
		alignItems: "center",
		width: "100%",
		height: "100%",
	},
	projectInfo: {
		padding: 16,
		flex: 1,
		justifyContent: "space-between",
	},
	projectTitle: {
		color: "#fff",
		fontSize: 18,
		fontWeight: "600",
		marginBottom: 8,
	},
	projectMeta: {
		justifyContent: "space-between",
	},
	projectTypeContainer: {
		flexDirection: "row",
		alignItems: "center",
		marginBottom: 6,
	},
	updateText: {
		color: "#666",
		fontSize: 12,
	},
	loadingContainer: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
	},
	loadingText: {
		color: "#666",
		fontSize: 16,
	},
	errorContainer: {
		flex: 1,
		justifyContent: "center",
		alignItems: "center",
	},
	errorText: {
		color: "#ff4444",
		fontSize: 16,
	},
	deleteButton: {
		position: "absolute",
		top: 8,
		right: 8,
		padding: 8,
		borderRadius: 4,
		backgroundColor: "rgba(0,0,0,0.5)",
		transition: "opacity 0.2s ease-in-out",
		cursor: "pointer",
		":hover": {
			backgroundColor: "rgba(0,0,0,0.7)",
		},
	},
	deleteConfirm: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0,0,0,0.9)",
		borderRadius: 8,
		justifyContent: "center",
		alignItems: "center",
		padding: 16,
	},
	deleteConfirmText: {
		color: "#fff",
		fontSize: 16,
		marginBottom: 16,
	},
	deleteConfirmButtons: {
		flexDirection: "row",
		gap: 12,
	},
	deleteConfirmButton: {
		paddingVertical: 8,
		paddingHorizontal: 16,
		borderRadius: 4,
	},
	deleteConfirmCancel: {
		backgroundColor: "#2A2A2A",
	},
	deleteConfirmDelete: {
		backgroundColor: "#ff4444",
	},
	deleteConfirmButtonText: {
		color: "#fff",
		fontSize: 14,
	},
	deleteConfirmButtonTextDelete: {
		fontWeight: "bold",
	},
	skeletonCard: {
		backgroundColor: "#2A2A2A",
	},
	skeletonThumbnail: {
		backgroundColor: "#333",
	},
	skeletonIcon: {
		width: 80,
		height: 14,
		backgroundColor: "#333",
		borderRadius: 4,
		marginTop: 8,
	},
	skeletonTitle: {
		width: 180,
		height: 18,
		backgroundColor: "#333",
		borderRadius: 4,
		marginBottom: 8,
	},
	skeletonUpdateText: {
		width: 100,
		height: 12,
		backgroundColor: "#333",
		borderRadius: 4,
		marginTop: 6,
	},
	whatsNewContainer: {
		padding: 20,
		maxWidth: 800,
		marginHorizontal: "auto",
	},
	whatsNewTitle: {
		fontSize: 28,
		fontWeight: "bold",
		color: "#fff",
		marginBottom: 8,
	},
	whatsNewSubtitle: {
		fontSize: 16,
		color: "#888",
		marginBottom: 30,
	},
	changelogEntry: {
		backgroundColor: "#2A2A2A",
		borderRadius: 12,
		padding: 20,
		marginBottom: 24,
		borderWidth: 1,
		borderColor: "#3A3A3A",
	},
	changelogHeader: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	versionContainer: {
		backgroundColor: "#4d2",
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 20,
	},
	versionText: {
		color: "#000",
		fontWeight: "bold",
		fontSize: 14,
	},
	dateText: {
		color: "#888",
		fontSize: 14,
	},
	entryTitle: {
		fontSize: 22,
		fontWeight: "bold",
		color: "#fff",
		marginBottom: 8,
	},
	entryDescription: {
		fontSize: 16,
		color: "#ddd",
		marginBottom: 20,
		lineHeight: 24,
	},
	branchesContainer: {
		marginBottom: 16,
	},
	branchesLabel: {
		color: "#888",
		fontSize: 14,
		fontWeight: "bold",
		marginBottom: 8,
	},
	branchesList: {
		flexDirection: "row",
		gap: 8,
	},
	branchTag: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#333",
		paddingHorizontal: 10,
		paddingVertical: 6,
		borderRadius: 6,
		borderWidth: 1,
		borderColor: "#555",
	},
	branchIcon: {
		marginRight: 6,
	},
	branchText: {
		color: "#fff",
		fontSize: 12,
		fontWeight: "500",
	},
	changesList: {
		marginTop: 12,
	},
	changeItem: {
		flexDirection: "row",
		alignItems: "flex-start",
		marginBottom: 12,
	},
	changeIcon: {
		marginRight: 10,
		marginTop: 2,
	},
	changeText: {
		color: "#ddd",
		fontSize: 15,
		flex: 1,
		lineHeight: 22,
	},
});

export default Home;
